import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { apiGet } from "@/shared/lib/api-client";
import { isAppModule } from "./appModules";
import { AuthContext, type AuthContextValue } from "./AuthContext";
import * as session from "./session";
import { ROLES, type AuthStatus, type Me, type Role } from "./types";

const CALLBACK_PATH = "/auth/callback";

/**
 * App-wide sign-in state. On start, if the browser has a session hint, it asks
 * the API for a fresh access token (HttpOnly cookie), so a page reload keeps
 * the user signed in.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [me, setMe] = useState<Me | null>(null);

  const loadMe = useCallback(async () => {
    try {
      setMe(await apiGet<Me>("/v1/me"));
      setStatus("signedIn");
    } catch {
      setMe(null);
      setStatus("signedOut");
    }
  }, []);

  useEffect(() => {
    // The callback page finishes the sign-in itself and then calls completeSignIn.
    // (Hosting may add a trailing slash: /auth/callback/.)
    if (window.location.pathname.replace(/\/+$/, "") === CALLBACK_PATH) return;
    // Never signed in on this browser: no need to ask the API.
    if (!session.hasSessionHint()) {
      setStatus("signedOut");
      return;
    }
    let cancelled = false;
    void session.refreshSession().then((ok) => {
      if (cancelled) return;
      if (ok) void loadMe();
      else setStatus("signedOut");
    });
    return () => {
      cancelled = true;
    };
  }, [loadMe]);

  useEffect(
    () =>
      session.onSessionEnd(() => {
        setMe(null);
        setStatus("signedOut");
      }),
    [],
  );

  const value = useMemo<AuthContextValue>(() => {
    const roles = (me?.groups ?? []).filter((g): g is Role =>
      (ROLES as readonly string[]).includes(g),
    );
    const modules = (me?.modules ?? []).filter(isAppModule);
    return {
      status,
      me,
      roles,
      hasRole: (...wanted) => wanted.some((role) => roles.includes(role)),
      isPlatformAdmin: roles.includes("platform-admin"),
      isPending: status === "signedIn" && roles.length === 0,
      modules,
      hasModule: (module) => modules.includes(module),
      login: (options) => session.startLogin(options),
      signup: (options) => session.startLogin({ ...options, signup: true }),
      logout: async () => {
        setStatus("loading");
        await session.logout();
      },
      completeSignIn: loadMe,
      // Roles come from the access token: get a new one first, so a role an
      // admin just granted shows up without signing out and in again.
      reloadMe: async () => {
        if (await session.refreshSession()) await loadMe();
        else setStatus("signedOut");
      },
    };
  }, [status, me, loadMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
